import React, { useState } from 'react';
import { Input, Textarea, Button } from "@material-tailwind/react";
import { db, storage } from './firebase';
import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  setDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import useImageUploader from './useImageUploader';
import { useUser } from '@clerk/clerk-react';

export default function CreateThoughtModal({ onClose }) {
  const { user } = useUser();
  const [text, setText] = useState('');
  const {
    imagePath,
    thumbnailPath,
    uploadImage,
    resetUploads,
    imageSize,
    thumbnailSize,
    originalFilePath,
    originalFileSize,
    originalMimeType,
  } = useImageUploader(user?.id);
  const [location, setLocation] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [tags, setTags] = useState('');


  const handleLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Require text or image:
    if (!text.trim() && (!imagePath || !thumbnailPath)) {
      return alert('Thought must include text or image');
    }
    if (!user?.id) {
      return alert('You must be signed-in to create a thought.');
    }

    setUploading(true);

    // 1) Normalize the tags into an array:
    const tagArray = tags
      .split(',')
      .map(t => t.trim().toLowerCase())
      .filter(t => t.length > 0);

    // 2) For each tag, write a user-level `/users/{userId}/tags/{tagName}` doc if it does not exist:
    for (const tag of tagArray) {
      const tagRef = doc(db, 'users', user.id, 'tags', tag);
      const tagSnap = await getDoc(tagRef);
      if (!tagSnap.exists()) {
        await setDoc(tagRef, {
          name: tag,
          createdAt: serverTimestamp()
        });
      }
    }

    // 3) Create the new Thought under /thoughts. Leverage serverTimestamp()
    //    on both createdAt (timestamp) and store the displayName.
    await addDoc(collection(db, 'thoughts'), {
      userId: user.id,
      displayName: user.fullName || user.primaryEmailAddress || 'Anonymous',
      text: text.trim(),
      imagePath: imagePath || null,
      thumbnailPath: thumbnailPath || null,
      originalFilePath: originalFilePath || null,
      originalFileSize: originalFileSize || 0,
      originalMimeType: originalMimeType || null,
      timestamp: serverTimestamp(),
      editedAt: null,
      location: location || null,
      tags: tagArray,
      imageSize: imageSize || 0,
      thumbnailSize: thumbnailSize || 0,
      aiStatus: 'pending',
      aiTags: null,
      aiCaption: null,
      aiEmbedding: null
    });

    // 4) Reset local input state and close:
    setText('');
    setLocation(null);
    setTags('');
    setUploading(false);
    resetUploads();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-70 flex items-center justify-center">
      <div className="bg-white w-full max-w-2xl mx-4 rounded-lg overflow-auto shadow-lg max-h-screen">
        <div className="flex justify-end p-4">
          <button
            className="text-gray-500 hover:text-gray-700 text-xl"
            onClick={onClose}
            aria-label="Close">
            &times;
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 pb-6">
          <div className="flex flex-col gap-2">
            <Textarea
              label="What's on your mind?"
              value={text}
              onChange={(e) => setText(e.target.value)}
              variant="standard"
              crossOrigin=""
            />
            <input
              type="file"
              accept="image/*"
              onChange={(e) => uploadImage(e.target.files[0])}
              className="border p-2 rounded w-full"
            />
            <Input
              label="Tags (comma-separated)"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              variant="standard"
              crossOrigin=""
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleLocation}
                title="Add Location"
                className="border border-gray-400 text-gray-800 px-3 py-2 rounded hover:border-black"
              >
                📍
              </button>
              <Button
                type="submit"
                disabled={uploading}
                color="blue"
                className="px-4"
              >
                {uploading ? 'Saving...' : 'Save'}
              </Button>
              <Button
                type="button"
                onClick={onClose}
                variant="outlined"
                className="text-gray-800 border-gray-400"
              >
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}